import { FC } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import {
  Typography,
  FormItem,
  Input,
  Icon,
  Table,
  tailwindOverride,
} from '@getsynapse/design-system';
import { selectPreviewTaskDates } from 'state/TasksBundle/tasksBundleSlice';
import { parseDateStr } from 'state/TasksBundle/helpers';
import { getProjectDataAttribute } from 'state/Project/projectSlice';
import { useLanguage } from 'Organisms/LanguageModal/LanguageContext';

const LOCALES_MAP: Record<string, string> = {
  en: 'en-US',
  fr: 'fr-FR',
};

const PreviewStep: FC = () => {
  const { data: tasks } = useSelector(selectPreviewTaskDates);
  const projectStartDate = useSelector(getProjectDataAttribute('startDate')) as
    | string
    | undefined;
  const { currentLocale } = useLanguage();

  const locale = LOCALES_MAP[currentLocale] || 'en-US';

  const formatDisplayDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    const { year, month, day } = parseDateStr(dateStr);
    return new Date(year, month - 1, day).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formattedProjectStartDate = formatDisplayDate(projectStartDate);

  return (
    <div className='flex flex-col gap-y-4'>
      <Typography variant='body' className='text-primary-darker'>
        {intl.get('TASKS.IMPORT_TASKS_MODAL.PREVIEW.DESCRIPTION')}
      </Typography>

      <FormItem
        label={intl.get('TASKS.IMPORT_TASKS_MODAL.PREVIEW.PROJECT_START_DATE')}
      >
        <Input value={formattedProjectStartDate} readOnly className='w-64' />
      </FormItem>

      <div
        className={tailwindOverride(
          'flex items-start gap-x-4',
          'px-6 py-3 rounded-sm',
          'border border-primary-lighter-two'
        )}
        style={{
          boxShadow: '4px 0 0 0 #687EB5 inset',
        }}
      >
        <Icon
          name='information-circle'
          className='text-2xl text-purple-dark flex-shrink-0'
        />
        <Typography variant='body' className='text-neutral-darker'>
          {intl.getHTML('TASKS.IMPORT_TASKS_MODAL.PREVIEW.NOTE')}
        </Typography>
      </div>

      <div className='max-h-64 overflow-y-auto'>
        <Table
          className='w-full'
          canSelectRows={false}
          data={{
            headData: {
              headCells: [
                {
                  content: intl.get(
                    'TASKS.IMPORT_TASKS_MODAL.PREVIEW.TASK_COLUMN'
                  ),
                  className: 'font-semibold',
                },
                {
                  content: intl.get(
                    'TASKS.IMPORT_TASKS_MODAL.PREVIEW.START_DATE_COLUMN'
                  ),
                  className: 'font-semibold w-36',
                },
                {
                  content: intl.get(
                    'TASKS.IMPORT_TASKS_MODAL.PREVIEW.END_DATE_COLUMN'
                  ),
                  className: 'font-semibold w-36',
                },
              ],
            },
            rows: tasks.map((task) => ({
              id: task.id,
              cells: [
                { content: task.name ?? '—' },
                { content: formatDisplayDate(task.calculatedStartDate) },
                { content: formatDisplayDate(task.calculatedEndDate) },
              ],
            })),
          }}
        />
      </div>
    </div>
  );
};

export default PreviewStep;
